import * as React from "react"
import { Auth0Context, useAuth0, type Auth0ContextInterface } from "@auth0/auth0-react"
import { clearBootSnapshot, type BootSnapshot } from "./snapshot"

type BootSnapshotContextType = {
  // the snapshot this page load renders from (null: normal boot)
  snapshot: BootSnapshot | null
  // Auth0 is still establishing the session and useAuth0() serves the
  // snapshot's user in the meantime
  optimistic: boolean
}

const BootSnapshotContext = React.createContext<BootSnapshotContextType>({ snapshot: null, optimistic: false })

type Props = {
  snapshot: BootSnapshot | null
  children?: React.ReactNode
  // injectable for tests
  onAccountMismatch?: () => void
}

const reloadWithoutSnapshot = () => {
  clearBootSnapshot()
  window.location.reload()
}

/**
 * Sits just inside Auth0Provider. While the SDK is still establishing the
 * session (silent auth / token refresh) and a boot snapshot exists, every
 * useAuth0() below sees the snapshot's user as signed in — so the app renders
 * immediately instead of behind "Setting up...". Token calls
 * (getAccessTokenSilently) are the SDK's own and wait for the real session;
 * no request is made on the snapshot's behalf. Once the SDK settles its real
 * state takes over: a failed session falls through to the normal login
 * redirect, and a different account than the snapshot's drops the snapshot
 * and reloads clean.
 */
export const BootSnapshotProvider = ({ snapshot, children, onAccountMismatch = reloadWithoutSnapshot }: Props) => {
  const auth0 = useAuth0()
  const accountMismatch = !!snapshot && !!auth0.user && auth0.user.sub !== snapshot.sub

  React.useEffect(() => {
    if (accountMismatch) onAccountMismatch()
  }, [accountMismatch, onAccountMismatch])

  const optimistic = !!snapshot && auth0.isLoading
  const value: Auth0ContextInterface = React.useMemo(
    () => (optimistic && snapshot ? { ...auth0, isLoading: false, isAuthenticated: true, user: snapshot.user } : auth0),
    [auth0, optimistic, snapshot],
  )
  const bootValue = React.useMemo(() => ({ snapshot: accountMismatch ? null : snapshot, optimistic }), [accountMismatch, snapshot, optimistic])

  return (
    <Auth0Context.Provider value={value}>
      <BootSnapshotContext.Provider value={bootValue}>{children}</BootSnapshotContext.Provider>
    </Auth0Context.Provider>
  )
}

export const useBootSnapshot = () => React.useContext(BootSnapshotContext)
